export interface MicrophoneStatus {
  permission: PermissionState | "unsupported" | "error";
  label: string;
}

export interface CapturedAudio {
  durationMs: number;
  mono: Float32Array;
  sampleRate: number;
}

export interface MicrophoneCapture {
  label: string;
  permission: PermissionState;
  stop: () => Promise<CapturedAudio>;
}

function concatFrames(frames: Float32Array[]): Float32Array {
  const totalSamples = frames.reduce((count, frame) => count + frame.length, 0);
  const output = new Float32Array(totalSamples);
  let offset = 0;

  frames.forEach((frame) => {
    output.set(frame, offset);
    offset += frame.length;
  });

  return output;
}

export async function requestMicrophonePermission(): Promise<MicrophoneStatus> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return { permission: "unsupported", label: "Microphone APIs unavailable" };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const track = stream.getAudioTracks()[0];
    const label = track?.label || "Default microphone";
    stream.getTracks().forEach((entry) => entry.stop());
    return { permission: "granted", label };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Microphone request failed";
    return { permission: "error", label: message };
  }
}

export async function startMicrophoneCapture(): Promise<MicrophoneCapture> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Microphone APIs unavailable");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      autoGainControl: false,
      channelCount: 1,
      echoCancellation: false,
      noiseSuppression: false,
    },
  });
  const context = new AudioContext({ latencyHint: "interactive" });
  const source = context.createMediaStreamSource(stream);
  const mutedSink = context.createGain();
  const frames: Float32Array[] = [];
  const startedAt = performance.now();
  mutedSink.gain.value = 0;

  if (context.state === "suspended") {
    await context.resume();
  }

  let disconnectNode: (() => void) | null = null;

  const attachScriptProcessor = () => {
    const processor = context.createScriptProcessor(1024, 1, 1);
    processor.onaudioprocess = (event) => {
      const channel = event.inputBuffer.getChannelData(0);
      frames.push(new Float32Array(channel));
    };
    source.connect(processor);
    processor.connect(mutedSink);
    mutedSink.connect(context.destination);
    disconnectNode = () => {
      processor.disconnect();
      mutedSink.disconnect();
      source.disconnect();
    };
  };

  if (typeof AudioWorkletNode !== "undefined") {
    try {
      await context.audioWorklet.addModule("/audio-worklets/capture-processor.js");
      const node = new AudioWorkletNode(context, "capture-processor");
      node.port.onmessage = (event: MessageEvent<{ samples?: number[] }>) => {
        if (!Array.isArray(event.data?.samples) || !event.data.samples.length) {
          return;
        }

        frames.push(Float32Array.from(event.data.samples));
      };
      source.connect(node);
      node.connect(mutedSink);
      mutedSink.connect(context.destination);
      disconnectNode = () => {
        node.disconnect();
        mutedSink.disconnect();
        source.disconnect();
      };
    } catch {
      attachScriptProcessor();
    }
  } else {
    attachScriptProcessor();
  }

  const label = stream.getAudioTracks()[0]?.label || "Default microphone";
  let stopped = false;

  return {
    label,
    permission: "granted",
    async stop() {
      if (stopped) {
        return {
          durationMs: 0,
          mono: new Float32Array(),
          sampleRate: context.sampleRate,
        };
      }

      stopped = true;
      disconnectNode?.();
      stream.getTracks().forEach((track) => track.stop());
      await context.close().catch(() => undefined);
      const mono = concatFrames(frames);

      return {
        durationMs: Math.round(performance.now() - startedAt),
        mono,
        sampleRate: context.sampleRate,
      };
    },
  };
}
